<template>
<b-card>
    <validation-observer ref="observer" v-slot="{ passes }">
    <b-form @submit.prevent="passes(onFormSubmit)" novalidate>
    
    <quick-entry-input
        v-model="quickEntryText"
        @parsed="onQuickEntryParsed"
        @error="onQuickEntryError"
        @warning="onQuickEntryWarnings"
        @selected-medication="onQuickEntryMedicationSelected"
        ref="quickEntry"
    />
    
    <hr class="my-3" />
    
    <validation-provider vid="non_field_errors" v-slot="{ errors }">
      <b-form-invalid-feedback :state="errors[0] ? false : null">
        <h6 class="d-block p-3 bg-danger">{{ errors[0] }}</h6>
      </b-form-invalid-feedback>
    </validation-provider>
    <validation-provider vid="repeat_prescription" v-slot="{ errors }">
    <b-form-group
        :label-size="small ? 'sm' : 'md'"
        label-cols-sm="2"
        label-cols-lg="3"
        :label="!isChronic ? 'Is Chronic' : ''"
        :invalid-feedback="errors[0]"
        :state="errors[0] ? false : null">
        <b-input-group class="mb-2">
            <b-input-group-prepend is-text v-if="!isChronic">
                <b-form-checkbox v-model="form.is_chronic" name="is_chronic"></b-form-checkbox>
            </b-input-group-prepend>
            <b-input-group-prepend is-text>repeat</b-input-group-prepend>
            <b-form-input type="number" :disabled="notChronic" v-model="form.repeat_prescription"></b-form-input>
            <b-input-group-prepend is-text>times after</b-input-group-prepend>
            <b-form-select :disabled="notChronic" id="repeat_date" name="repeat_date" plain v-model="form.repeat_date_id">
                <b-form-select-option disabled :value="null">Select Repeat Date</b-form-select-option>
                <b-form-select-option v-for="repeat_date in medication_repeat_dates" :key="repeat_date.id" :value="repeat_date.id">{{ repeat_date.name }}
                </b-form-select-option>
            </b-form-select>
        </b-input-group>
        <validation-provider vid="repeat_date_id" v-slot="{ errors }">
            <b-form-invalid-feedback :state="errors[0] ? false : null">
                {{ errors[0] }}
            </b-form-invalid-feedback>
        </validation-provider>
    </b-form-group>
    </validation-provider>

    <validation-provider vid="medication_id" v-slot="{ errors }">
        <b-form-group
            :disabled="hasAlternateMedication"
            :label-size="small ? 'sm' : 'md'"
            label-cols-sm="2"
            label-cols-lg="3"
            label="Medication"
            :invalid-feedback="errors[0]"
            :state="errors[0] ? false : null">
            <vue-bootstrap-autocomplete v-model="query" :data="items" placeholder="Search Medication..." :minMatchingChars="1" :serializer="medicationSerializer" @hit="onHitMedication" @input="lookupItem">
                    <template slot="suggestion" slot-scope="{ data, htmlText }">
                        <div class="d-flex align-items-center">
                            <!-- Note: the v-html binding is used, as htmlText contains
                            the suggestion text highlighted with <strong> tags -->
                            <span class="ml-1" v-html="htmlText"></span>
                            <span class="ml-auto">{{ data.code }}</span>
                        </div>
                    </template>

                <template #append>
                    <b-button variant="warning" @click="query = ''"><i class="fa-regular fa-arrows-rotate-reverse"></i> Clear</b-button>
                </template>
            </vue-bootstrap-autocomplete>
        </b-form-group>
    </validation-provider>
    <validation-provider vid="other" v-slot="{ errors }">
        <b-form-group
            :label-size="small ? 'sm' : 'md'"
            label-cols-sm="2"
            label-cols-lg="3"
            label="Other"
            description="Does not check on allergies."
            :invalid-feedback="errors[0]"
            :state="errors[0] ? false : null">
            <b-input-group>
                <b-input-group-prepend is-text>
                    <b-form-checkbox v-b-tooltip.hover title="" v-model="hasAlternateMedication" @change="onAlternateMedicationChange">
                </b-form-checkbox>
                </b-input-group-prepend>
                <b-form-input id="other" :disabled="!hasAlternateMedication" name="other" placeholder="Other medication" v-model="form.other" />
            </b-input-group>
        </b-form-group>
    </validation-provider>
    <validation-provider name="Strength" vid="strength" rules="strength" v-slot="{ errors }">
        <b-form-group
            :label-size="small ? 'sm' : 'md'"
            label-cols-sm="2"
            label-cols-lg="3"
            label="Strength"
            :invalid-feedback="errors[0]"
            :state="errors[0] ? false : null">
            <b-input-group>
                <b-form-input type="number" id="strength" v-comma2dot name="strength" placeholder=".00" v-model="form.strength" />
                <template #append>
                    <b-form-select id="unit_id" name="unit_id" class="squared-left" plain v-model="form.unit_id">
                     <b-form-select-option disabled :value="null">Select Unit</b-form-select-option>
                    <b-form-select-option v-for="unit in units" :key="unit.id" :value="unit.id">{{ unit.name }}</b-form-select-option>
                    </b-form-select>
                </template>
            </b-input-group>
             <validation-provider vid="unit_id" v-slot="{ errors }">
                <b-form-invalid-feedback :state="errors[0] ? false : null">
                    {{ errors[0] }}
                  </b-form-invalid-feedback>
            </validation-provider>
        </b-form-group>
    </validation-provider>

    <validation-provider name="Dosage" vid="frequency_usage" rules="double:2" v-slot="{ errors }">
    <b-form-group
         :label-size="small ? 'sm' : 'md'"
        label-cols-sm="2"
        label-cols-lg="3"
        label="Dosage"
        :invalid-feedback="errors[0]"
        :state="errors[0] ? false : null">
         <b-input-group>
            <b-form-input type="number" id="frequency_usage" name="frequency_usage" v-model="form.frequency_usage" placeholder="Frequency usage..." autocomplete="off" />
             <b-input-group-prepend is-text>
                times per
            </b-input-group-prepend>
            <!-- <b-form-input value="times per" readonly autocomplete="off" /> -->
            <b-form-select id="frequency_unit_id" name="frequency_unit_id" plain v-model="form.frequency_unit_id">
                <b-form-select-option disabled :value="null">Select Frequency Unit</b-form-select-option>
                <b-form-select-option v-for="frequency_unit in frequency_units" :key="frequency_unit.id" :value="frequency_unit.id">{{ frequency_unit.name }}
                </b-form-select-option>
            </b-form-select>
        </b-input-group>
        <validation-provider vid="frequency_unit_id" v-slot="{ errors }">
            <b-form-invalid-feedback :state="errors[0] ? false : null">
                {{ errors[0] }}
            </b-form-invalid-feedback>
        </validation-provider>
    </b-form-group>
    </validation-provider>

    <validation-provider name="Amount" vid="dosage_amount" v-slot="{ errors }">
    <b-form-group
        :label-size="small ? 'sm' : 'md'"
        label-cols-sm="2"
        label-cols-lg="3"
        label="Amount"
        description="example: 3 times per day 2 tablets"
        :invalid-feedback="errors[0]"
        :state="errors[0] ? false : null">
        <b-input-group>
        <b-form-input type="number" id="dosage_amount" v-comma2dot name="dosage_amount" v-model="form.dosage_amount" placeholder="Dosage amount..." autocomplete="off" />
        <b-form-select id="dosage_form_id" name="dosage_form_id" plain v-model="form.dosage_form_id">
            <b-form-select-option disabled :value="null">Select Dosage form</b-form-select-option>
            <b-form-select-option v-for="prescription_dosage_form in prescription_dosage_forms" :key="prescription_dosage_form.id" :value="prescription_dosage_form.id">{{ prescription_dosage_form.name }}
            </b-form-select-option>
        </b-form-select>
        </b-input-group>
        <validation-provider vid="dosage_form_id" v-slot="{ errors }">
            <b-form-invalid-feedback :state="errors[0] ? false : null">
                {{ errors[0] }}
            </b-form-invalid-feedback>
        </validation-provider>
    </b-form-group>
    </validation-provider>

    <validation-provider vid="delivery_form_id" v-slot="{ errors }">
        <b-form-group
            :label-size="small ? 'sm' : 'md'"
            label-cols-sm="2"
            label-cols-lg="3"
            label="Administration"
            :invalid-feedback="errors[0]"
            :state="errors[0] ? false : null">

            <b-form-select id="delivery_form_id" name="delivery_form_id" plain v-model="form.delivery_form_id">
                <b-form-select-option disabled :value="null">Select Delivery Form</b-form-select-option>
                <b-form-select-option v-for="delivery_form in delivery_forms" :key="delivery_form.id" :value="delivery_form.id">{{ delivery_form.name }}
                </b-form-select-option>
            </b-form-select>
        </b-form-group>
    </validation-provider>

    <validation-provider vid="amount_of_time" v-slot="{ errors }">
        <b-form-group
            :label-size="small ? 'sm' : 'md'"
            label-cols-sm="2"
            label-cols-lg="3"
            label="Period"
            :invalid-feedback="errors[0]"
            :state="errors[0] ? false : null">
                <b-input-group>
                    <b-form-input type="number" id="amount_of_time" name="amount_of_time" v-model="form.amount_of_time" placeholder="Amount of time" autocomplete="off" />
                     <b-input-group-prepend is-text>
                        Total
                    </b-input-group-prepend>
                    <b-form-input readonly id="total_amount" :value="calculatedTotal" autocomplete="off" />
                </b-input-group>
        </b-form-group>
    </validation-provider>

    <b-form-group
        :label-size="small ? 'sm' : 'md'"
        label="Administration Instructions"
        label-for="instructions">
            <b-form-textarea v-shortcut-expand id="instructions" v-capitalize v-model="form.instructions" placeholder="Add administration instructions..." rows="5"></b-form-textarea>
    </b-form-group>

    <b-form-group
        :label-size="small ? 'sm' : 'md'"
        label="Administrative date"
        label-for="administrative_datetime">
            <b-form-datepicker id="administrative_datetime" placeholder="Set administrative date..." v-model="form.administrative_datetime" :locale="currentLang" :max="maxDate"></b-form-datepicker>
    </b-form-group>

    <b-button class="mr-1" type="submit" variant="success"><i class="fa-regular fa-circle-dot"></i> Submit</b-button>
    <b-button v-if="isDirty || hasErrors" type="button" variant="danger" @click="reset"><i class="fa-solid fa-ban"></i> Reset</b-button>
        
    <TemplateButton ref="prescriptionTemplateButton" />
    </b-form>
    </validation-observer>
</b-card>
</template>
<script>
import FormExtension from "@/core/components/FormExtension"
import { mapGetters, mapState, mapActions } from "vuex"
import TemplateButton from './templates/TemplateButton'
import QuickEntryInput from './QuickEntryInput'

import debounce from 'lodash/debounce'

export default FormExtension.extend({
    name: "prescriptions-form",
    components: {
        TemplateButton,
        QuickEntryInput
    },
    props: {
        isChronic: {
            type: Boolean,
            default: false
        },
        decursusId: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            items: [],
            form: null,
            query: "",
            quickEntryText: "",
            hasAlternateMedication: false,
            maxDate: new Date(),
        }
    },
    computed: {
        ...mapState('catalogue', {
            // medications: state => state.medications,
            units: state => state.units,
            frequency_units: state => state.frequency_units,
            prescription_dosage_forms: state => state.prescription_dosage_forms,
            delivery_forms: state => state.delivery_forms,
            side_effects_statuses: state => state.side_effects_statuses,
            medication_repeat_dates: state => state.medication_repeat_dates,
            severities: state => state.severities
        }),

        calculatedTotal() {
            return this.form.frequency_usage * this.form.dosage_amount * (this.form.amount_of_time ? this.form.amount_of_time : 1)
        },

        notChronic() {
            return this.isChronic ? false : this.form.is_chronic == false
        }
    },
    watch: {
        form: {
            deep: true,
            handler(val) {
                this.isDirty = this.form.dirty()

                if (this.isDirty) {
                    this.dirty()

                }

                this.$refs.prescriptionTemplateButton.prepareTemplate(val.data())
            }
        },
        catalogue_defaults: {
            immediate: true,
            handler(val) {
                this.newFormWithDefaults(val)
            }
        }
    },
    methods: {
        ...mapActions('catalogue', ['getCatalogueItem']),
        ...mapActions('prescriptions', ['filterMedication', 'createPrescriptionTemplate', 'updatePrescriptionTemplate']),
        ...mapActions('health', ['checkForAllergy']),
        
        onQuickEntryParsed(parsedData) {
            const source = parsedData?.form || parsedData || {}
            const formData = {
                medication_id: source.medication_id ?? null,
                other: source.other || '',
                strength: source.strength ?? null,
                unit_id: source.unit_id ?? null,
                frequency_usage: source.frequency_usage ?? null,
                frequency_unit_id: source.frequency_unit_id ?? null,
                dosage_amount: source.dosage_amount ?? null,
                dosage_form_id: source.dosage_form_id ?? null,
                amount_of_time: source.amount_of_time ?? null,
                delivery_form_id: source.delivery_form_id ?? null,
                instructions: source.instructions || '',
                administrative_datetime: source.administrative_datetime || null,
                is_chronic: Boolean(source.is_chronic),
                repeat_prescription: source.repeat_prescription ?? 0,
                repeat_date_id: source.repeat_date_id ?? null
            }
            
            this.form.populate(formData)
            
            const medicationName = parsedData?.meta?.medication_name || parsedData?.medication_name
            if (medicationName) {
                this.query = medicationName
            }
            
            this.$emit('quick-entry-applied', parsedData)
        },

        onQuickEntryWarnings(warnings = []) {
            const firstWarning = warnings[0]
            if (!firstWarning?.message) {
                return
            }
            this.notify('Quick Entry Warning', firstWarning.message, 'warning')
        },

        onQuickEntryMedicationSelected(medication) {
            if (!medication?.id) {
                return
            }
            this.hasAlternateMedication = false
            this.query = medication.name || this.query
            this.form.populate({
                medication_id: medication.id,
                other: ''
            })
        },
        
        onQuickEntryError(error) {
            this.$emit('quick-entry-error', error)
        },
        
        onAlternateMedicationChange(value) {
            // clear date form data
            this.form.populate({medication_id: null, other: ""})
            this.query = ""
        },
        medicationSerializer(item) {
            return item.name
        },
        async onHitMedication(item) {
            const check = await this.checkForAllergy({ allergy: item.code })
            if (check) {
                this.notify("Patient Warning", "Patient is allergic for this medication.", "warning")
                this.$refs.observer?.setErrors({"medication_id": ["Patient is allergic to this medication."]})
            }else{
                this.$nextTick(() => {
                    this.$refs.observer?.reset()
                })
            }
            
            this.form.populate({medication_id: item.id})
        },
        lookupItem: debounce(function () {
            this.filterMedication({ search: this.query })
                .then((items) => {
                    this.items = items
            })
        }, 500),
        onFormSubmit() {
            this.callback(this.form.data()).then(() => {
                this.reset()
                this.notify("Patient prescription created", "Patient prescription was created successfully")
                this.$emit('created')
            })
            .catch(errors => {
                if (errors.response) {
                    this.$refs.observer?.setErrors(errors.response.data)
                    this.hasErrors = true
                }
            })
        },
        newFormWithDefaults(defaults) {
            this.form = Form.create({
                medication_id: null,
                is_chronic: this.isChronic,
                other: "",
                strength: null,
                unit_id: null,
                frequency_usage: null,
                frequency_unit_id: null,
                dosage_amount: null,
                dosage_form_id: null,
                amount_of_time: null,
                delivery_form_id: null,
                instructions: "",
                stoppage_reason: "",
                repeat_prescription: this.isChronic ? defaults?.repeat_prescription : 0,
                repeat_date_id: this.isChronic ? defaults?.repeat_date : null,
                administrative_datetime: null,
                consistency_token: null
            })
        },
        reset() {
            this.form.reset()
            this.newFormWithDefaults(this.catalogue_defaults)
            this.quickEntryText = ''
            this.$refs.quickEntry?.clearEntry()

            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.query = ""
                this.hasAlternateMedication = false
                this.hasErrors = false
                this.validated()
            })

            EventBus.$emit('prescription-reset-active-template')
            EventBus.$emit('prescription-reset-template')
        },
        copyPrescription(val) {
            if(val){
                if (val.medication?.name) {
                    this.query = val.medication.name
                }
                this.form.populate(val)
            }
        },
        copyPrescriptionFromTemplate(val) {
            if(val){
                // Use medication.name or other for query, not the full name with strength
                if (val.medication?.name) {
                    this.query = val.medication.name
                } else if (val.other) {
                    this.hasAlternateMedication = true
                }

                this.form.populate({
                    medication_id: val.dosage?.medication_id,
                    other: val.dosage?.other,
                    is_chronic: val.dosage?.is_chronic || false,
                    repeat_prescription: val.dosage?.repeat_prescription || 0,
                    repeat_date_id: val.dosage?.repeat_date_id || null,
                    strength: val.dosage?.strength,
                    unit_id: val.dosage?.unit_id,
                    frequency_usage: val.dosage?.frequency_usage,
                    frequency_unit_id: val.dosage?.frequency_unit_id,
                    dosage_amount: val.dosage?.dosage_amount,
                    dosage_form_id: val.dosage?.dosage_form_id,
                    amount_of_time: val.dosage?.amount_of_time,
                    delivery_form_id: val.dosage?.delivery_form_id,
                    instructions: val.dosage?.instructions,
                })
            }else{
                this.reset()
            }
        }
    },
    async mounted() {
        // await this.getCatalogueItem('medications')
        await this.getCatalogueItem('units')
        await this.getCatalogueItem('frequency_units')
        await this.getCatalogueItem('prescription_dosage_forms')
        await this.getCatalogueItem('delivery_forms')
        await this.getCatalogueItem('side_effects_statuses')
        await this.getCatalogueItem('medication_repeat_dates')
        await this.getCatalogueItem('severities')

        EventBus.$on('prescription-copied', (copied) => {
            this.copyPrescription(copied)
        })
        EventBus.$on('prescription-copied-from-template', (copied) => {
            this.copyPrescriptionFromTemplate(copied)
        })
    },
    beforeDestroy() {
         EventBus.$off("prescription-copied")
         EventBus.$off("prescription-copied-from-template")
    }
})
</script>
